import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

export function useAdminAnalytics(from, to) {
  const [overview, setOverview]   = useState(null);
  const [loading, setLoading]     = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/analytics/overview', { params: { from, to } });
      setOverview(data);
    } catch {}
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { fetch(); }, [fetch]);
  return { overview, loading, refetch: fetch };
}

export function useAdminCoupons() {
  const [coupons, setCoupons]   = useState([]);
  const [meta, setMeta]         = useState({});
  const [loading, setLoading]   = useState(true);
  // Filter state lives in the hook so refetches triggered by modal-close
  // and other "refresh after action" callsites don't silently drop the
  // status / search the user picked. Previously the hook fired a no-arg
  // fetchCoupons() on mount AND modal-close paths also called
  // fetchCoupons() with no args — both routes overwrote the filtered
  // table with an unfiltered response. Worse, the mount fetch raced
  // with the user's first dropdown pick (#9 from Ananya's 06-25 batch).
  const filtersRef = useRef({ search: '', status: '' });

  const fetchCoupons = useCallback(async (params) => {
    setLoading(true);
    try {
      // Explicit `params` becomes the new filter baseline. No-arg calls
      // ("just refresh the current view") reuse whatever filters were
      // last applied — we read off a ref so we don't have to invalidate
      // the callback identity on every filter change.
      const next = params !== undefined ? { ...filtersRef.current, ...params } : filtersRef.current;
      filtersRef.current = next;
      const query = {
        search: next.search || undefined,
        status: next.status || undefined,
      };
      const { data } = await api.get('/admin/coupons', { params: query });
      setCoupons(data.data);
      setMeta(data.meta ?? data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  // Initial load. The component-side debounce useEffect (in CouponsTab)
  // also fires on mount, so kicking off the request here would race —
  // skip the initial-load fetch and let the component drive it. The
  // component handles both first paint and filter changes through the
  // same code path, which is what fixed the dropdown-doesn't-filter
  // bug.

  const deleteCoupon = async (id) => {
    await api.delete(`/admin/coupons/${id}`);
    setCoupons(prev => prev.filter(c => c.id !== id));
  };

  // Reversible alternative to deletion, used for approved merchant coupons
  // where a hard delete would silently destroy someone else's live offer.
  const setCouponActive = async (id, isActive) => {
    const { data } = await api.patch(`/admin/coupons/${id}`, { is_active: isActive });
    const updated = data?.data ?? data;
    setCoupons(prev => prev.map(c => (c.id === id ? { ...c, ...updated, is_active: isActive } : c)));
  };

  return { coupons, meta, loading, fetchCoupons, deleteCoupon, setCouponActive };
}

// Which merchants an admin may act on. An empty list means "no restriction",
// not "no access" — see the migration note in perkvora-web.
export async function fetchMerchantScope(userId) {
  const { data } = await api.get(`/admin/users/${userId}/merchant-scope`);
  return data.data ?? data;
}

export async function saveMerchantScope(userId, merchantIds) {
  const { data } = await api.put(`/admin/users/${userId}/merchant-scope`, { merchant_ids: merchantIds });
  return data;
}

export function useAdminMerchants() {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading]     = useState(true);

  const fetchMerchants = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/merchants', { params });
      setMerchants(data.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMerchants(); }, [fetchMerchants]);

  const verifyMerchant = async (id) => {
    await api.post(`/admin/merchants/${id}/verify`);
    setMerchants(prev => prev.map(m => m.id === id ? { ...m, is_verified: true, is_active: true } : m));
  };

  const rejectMerchant = async (id, reason = '') => {
    await api.post(`/admin/merchants/${id}/reject`, { reason });
    setMerchants(prev => prev.map(m => m.id === id ? { ...m, is_verified: false, is_active: false } : m));
  };

  const deleteMerchant = async (id) => {
    await api.delete(`/admin/merchants/${id}`);
    setMerchants(prev => prev.filter(m => m.id !== id));
  };

  return { merchants, loading, fetchMerchants, verifyMerchant, rejectMerchant, deleteMerchant };
}

// Lightweight separate hook for the pending-approvals panel so the
// counter and list stay live without coupling to the main merchant
// listing (which is paginated and may not include older unverified rows).
export function usePendingMerchants() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/merchants', { params: { verified: 'false', per_page: 50 } });
      // /admin/merchants returns a Laravel paginator JSON — items live on data.data.
      setPending(Array.isArray(data?.data) ? data.data : []);
    } catch { setPending([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { pending, loading, refresh };
}

export function useSettings(group = '') {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/settings', { params: group ? { group } : {} });
      setRows(data.data || []);
    } catch { setRows([]); } finally { setLoading(false); }
  }, [group]);
  useEffect(() => { refresh(); }, [refresh]);

  const bulkUpdate = useCallback(async (updates) => {
    const { data } = await api.patch('/admin/settings/bulk', { updates });
    await refresh();
    return data;
  }, [refresh]);

  return { rows, loading, refresh, bulkUpdate };
}

export async function testIntegration(name) {
  const { data } = await api.post(`/admin/integrations/${name}/test`);
  return data;
}

export function useBanners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/banners'); setBanners(data.data || []); }
    catch { setBanners([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { banners, loading, refresh };
}

export function useBlog() {
  const [posts, setPosts]           = useState([]);
  const [meta, setMeta]             = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filters, setFilters]       = useState({ status: 'all', q: '', page: 1 });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get('/admin/blog/posts', { params: filters }),
        api.get('/admin/blog/categories'),
      ]);
      setPosts(p.data.data || []); setMeta(p.data.meta || {});
      setCategories(c.data.data || []);
    } catch { /* keep prev */ } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { refresh(); }, [refresh]);
  return { posts, meta, categories, loading, filters, setFilters, refresh };
}

export function useMailSettings() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/mail-settings'); setData(data); }
    catch { setData(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { data, loading, refresh };
}

export async function updateMailSettings(updates) {
  const { data } = await api.put('/admin/mail-settings', { updates });
  return data;
}

export async function clearMailOverride(field) {
  const { data } = await api.delete('/admin/mail-settings/override', { data: { field } });
  return data;
}

export async function sendMailTest(to) {
  const { data } = await api.post('/admin/mail-settings/test', { to });
  return data;
}

export function usePaymentSettings() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/payment-settings'); setData(data); }
    catch { setData(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { data, loading, refresh };
}

export async function updatePaymentSettings(updates) {
  const { data } = await api.put('/admin/payment-settings', { updates });
  return data;
}

export async function clearPaymentOverride(field) {
  const { data } = await api.delete('/admin/payment-settings/override', { data: { field } });
  return data;
}

export async function testPaymentConnection() {
  const { data } = await api.post('/admin/payment-settings/test');
  return data;
}

export function useRevenue(period = '30d', appClientId = '') {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get('/admin/analytics/revenue', { params: { period, app_client_id: appClientId || undefined } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period, appClientId]);
  return { data, loading };
}

export function useTierLifecycle(nudgeBand = 10) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get('/admin/analytics/tier-lifecycle', { params: { nudge_band: nudgeBand } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [nudgeBand]);
  return { data, loading };
}

export function useOnboardingFunnel() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/admin/analytics/funnel')
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  return { data, loading };
}

export function useEmailHealth(days = 30) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/admin/analytics/email-health', { params: { days } })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);
  return { data, loading };
}

export function useCohorts(metric = 'redemptions', months = 12) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get('/admin/analytics/cohorts', { params: { metric, months } })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [metric, months]);
  return { data, loading };
}

export function useFeatureFlags() {
  const [flags, setFlags]     = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/feature-flags'); setFlags(data.data || []); }
    catch { setFlags([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { flags, loading, refresh };
}

export async function previewBroadcast(payload) {
  const { data } = await api.post('/admin/broadcasts/preview', payload);
  return data;
}

export async function sendBroadcast(payload) {
  const { data } = await api.post('/admin/broadcasts', payload);
  return data;
}

// ── Campaign lifecycle (draft → scheduled → sent | cancelled) ──
// Shares the same notification_campaigns table as instant broadcasts. The
// admin can save a draft, schedule it for a future time, manually send it,
// cancel it before it fires, or delete drafts/cancelled rows.

export function useBroadcasts() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/broadcasts'); setRows(data.data || []); }
    catch { setRows([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { rows, loading, refresh };
}

export async function saveBroadcastDraft(payload) {
  // payload may include scheduled_at (ISO string) → backend flips status to 'scheduled'
  const { data } = await api.post('/admin/broadcasts/drafts', payload);
  return data;
}

export async function updateBroadcast(id, payload) {
  const { data } = await api.put(`/admin/broadcasts/${id}`, payload);
  return data;
}

export async function cancelBroadcast(id) {
  const { data } = await api.post(`/admin/broadcasts/${id}/cancel`);
  return data;
}

export async function runBroadcastNow(id) {
  const { data } = await api.post(`/admin/broadcasts/${id}/send`);
  return data;
}

export async function deleteBroadcast(id) {
  const { data } = await api.delete(`/admin/broadcasts/${id}`);
  return data;
}

export function useSystemHealth() {
  const [health, setHealth]   = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/health'); setHealth(data); }
    catch { setHealth(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { health, loading, refresh };
}

export function useWebhooks() {
  const [webhooks, setWebhooks]       = useState([]);
  const [availableEvents, setEvents]  = useState([]);
  const [loading, setLoading]         = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/webhooks');
      setWebhooks(data.data || []);
      setEvents(data.available_events || []);
    } catch { setWebhooks([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { webhooks, availableEvents, loading, refresh };
}

export async function bulkUserAction(action, userIds) {
  const { data } = await api.post('/admin/users/bulk', { action, user_ids: userIds });
  return data;
}

export async function startImpersonation(userId) {
  const { data } = await api.post(`/admin/users/${userId}/impersonate`);
  return data;
}

export async function stopImpersonation() {
  const { data } = await api.post('/admin/impersonate/stop');
  return data;
}

export function useAdminUsers(initialQuery = '') {
  const [users, setUsers]     = useState([]);
  const [meta, setMeta]       = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery]     = useState(initialQuery);
  const [includePending, setIncludePending] = useState(false);
  // debouncedQuery lags `query` so every keystroke updates the input
  // instantly but only the settled value triggers a backend hit.
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  const [page, setPage]       = useState(1);

  useEffect(() => {
    // 1-char queries trash MySQL with leading-wildcard LIKEs and
    // produce uselessly broad result sets. Skip them.
    const trimmed = (query || '').trim();
    const next    = trimmed.length === 1 ? '' : trimmed;
    const t = setTimeout(() => { setDebouncedQuery(next); setPage(1); }, 450);
    return () => clearTimeout(t);
  }, [query]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/users', {
        params: {
          q: debouncedQuery || undefined,
          page,
          per_page: 25,
          include_pending_signups: includePending ? 1 : undefined,
        },
      });
      setUsers(data.data || []);
      setMeta(data.meta || {});
    } catch { setUsers([]); setMeta({}); } finally { setLoading(false); }
  }, [debouncedQuery, page, includePending]);

  useEffect(() => { refresh(); }, [refresh]);

  return { users, meta, loading, query, setQuery, page, setPage, includePending, setIncludePending, refresh };
}

export function useRoleCatalogue() {
  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/admin/roles')
      .then(({ data }) => setRoles(data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  return { roles, loading };
}

export async function fetchUserRoles(userId) {
  const { data } = await api.get(`/admin/roles/users/${userId}`);
  return data;
}

export async function assignUserRole(userId, role) {
  const { data } = await api.post(`/admin/roles/users/${userId}`, { role });
  return data;
}

export async function revokeUserRole(userId, role) {
  const { data } = await api.delete(`/admin/roles/users/${userId}/${role}`);
  return data;
}

export function useAuditLogs(initialFilters = {}) {
  const [logs, setLogs]           = useState([]);
  const [meta, setMeta]           = useState({});
  const [loading, setLoading]     = useState(true);
  const [filters, setFilters]     = useState({ page: 1, per_page: 30, ...initialFilters });

  const refresh = useCallback(async (override) => {
    setLoading(true);
    try {
      const params = override || filters;
      const cleaned = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null));
      const { data } = await api.get('/admin/audit-logs', { params: cleaned });
      setLogs(data.data || []);
      setMeta(data.meta || {});
    } catch { setLogs([]); setMeta({}); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { refresh(); }, [refresh]);

  return { logs, meta, loading, filters, setFilters, refresh };
}

export function useAuditStats(days = 30) {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/admin/audit-logs/stats', { params: { days } })
      .then(({ data }) => setStats(data || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);
  return { stats, loading };
}

export function useAppClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/app-clients');
      setClients(data);
    } catch { /* leave previous list intact */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const updateClient = useCallback(async (id, payload) => {
    const { data } = await api.put(`/admin/app-clients/${id}`, payload);
    setClients(prev => prev.map(c => c.id === id ? { ...c, ...data.data } : c));
    return data.data;
  }, []);

  // Credentials are never part of the list payload — the server strips them.
  // These three fetch or re-issue them on demand, and every call is audit
  // logged server-side.
  const fetchCredentials = useCallback(async (id) => {
    const { data } = await api.get(`/admin/app-clients/${id}/credentials`);
    return data;
  }, []);

  const rotateSecret = useCallback(async (id) => {
    const { data } = await api.post(`/admin/app-clients/${id}/rotate-secret`);
    return data;
  }, []);

  const regenerateKey = useCallback(async (id) => {
    const { data } = await api.post(`/admin/app-clients/${id}/regenerate-key`);
    return data;
  }, []);

  return { clients, loading, setClients, refresh, updateClient, fetchCredentials, rotateSecret, regenerateKey };
}

export function useAffiliateFeeds() {
  const [feeds, setFeeds]               = useState([]);
  const [networks, setNetworks]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');

  const fetch = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/admin/affiliate-feeds');
      setFeeds(data.data || []);
      setNetworks(data.available_networks || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load feeds');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const createFeed = async (payload) => {
    const { data } = await api.post('/admin/affiliate-feeds', payload);
    await fetch();
    return data;
  };

  const updateFeed = async (id, payload) => {
    const { data } = await api.patch(`/admin/affiliate-feeds/${id}`, payload);
    await fetch();
    return data;
  };

  const deleteFeed = async (id) => {
    await api.delete(`/admin/affiliate-feeds/${id}`);
    setFeeds(prev => prev.filter(f => f.id !== id));
  };

  const syncFeed = async (id, queue = false) => {
    const { data } = await api.post(`/admin/affiliate-feeds/${id}/sync`, null, { params: { queue: queue ? 1 : 0 } });
    await fetch();
    return data;
  };

  return { feeds, networks, loading, error, refetch: fetch, createFeed, updateFeed, deleteFeed, syncFeed };
}

export function useCouponSubmissions(initialStatus = 'pending') {
  const [submissions, setSubmissions] = useState([]);
  const [meta, setMeta]               = useState({});
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [status, setStatus]           = useState(initialStatus);

  const fetch = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/admin/coupon-submissions', { params: { status } });
      setSubmissions(data.data || []);
      setMeta(data.meta ?? data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load submissions');
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { fetch(); }, [fetch]);

  const approve = async (id, payload) => {
    await api.post(`/admin/coupon-submissions/${id}/approve`, payload);
    await fetch();
  };

  const reject = async (id, reason) => {
    await api.post(`/admin/coupon-submissions/${id}/reject`, { reason });
    await fetch();
  };

  return { submissions, meta, loading, error, status, setStatus, refetch: fetch, approve, reject };
}

export function useAdminCashbackPools() {
  const [pools, setPools]     = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/cashback-pools');
      setPools(data.data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const createPool = async (payload) => {
    const { data } = await api.post('/admin/cashback-pools', payload);
    await fetch();
    return data;
  };

  const updatePool = async (id, payload) => {
    const { data } = await api.put(`/admin/cashback-pools/${id}`, payload);
    await fetch();
    return data;
  };

  const deletePool = async (id) => {
    await api.delete(`/admin/cashback-pools/${id}`);
    setPools(prev => prev.filter(p => p.id !== id));
  };

  return { pools, loading, refetch: fetch, createPool, updatePool, deletePool };
}
